/**
 * The one place a request to the CRM API is made.
 *
 * Two things happen on every call that a caller must not have to remember:
 *   * `ensureFresh()` first, so no request goes out carrying an expired access token;
 *   * a 401 triggers one refresh-and-replay, which is the fallback for the case the pre-emptive
 *     refresh cannot cover - a token the server revoked rather than one that aged out.
 *
 * Errors arrive as `ApiError` carrying the server's own `{ error }` sentence. That matters more here
 * than in the other front ends: this API answers a refused action with a 400 and a sentence written
 * for the technician ("The device is offline, so this is queued"), and collapsing that to a status
 * code would throw away the only useful part.
 */
import { ApiError, API_BASE, bearer, ensureFresh, errorFrom, recoverFrom401 } from './auth.js';

export { ApiError } from './auth.js';

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Set for a multipart upload, where the browser must choose the boundary itself. */
  raw?: BodyInit;
  /** Suppresses the refresh-and-replay. Used by the refresh call, which must not recurse. */
  noRetry?: boolean;
  signal?: AbortSignal;
}

async function failureFrom(response: Response): Promise<ApiError> {
  let message = `${response.status} ${response.statusText}`;
  try {
    const text = await response.text();
    message = errorFrom(text) ?? (text.trim() ? text.trim() : message);
  } catch {
    /* not a body we can read - keep the status line */
  }
  return new ApiError(response.status, message);
}

function headersFor(options: RequestOptions): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = bearer();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (options.raw === undefined && options.body !== undefined) headers['Content-Type'] = 'application/json';
  return headers;
}

function bodyFor(options: RequestOptions): BodyInit | undefined {
  if (options.raw !== undefined) return options.raw;
  if (options.body !== undefined) return JSON.stringify(options.body);
  return undefined;
}

/** A JSON call. `T` is `void` for a 204, which this returns as undefined rather than parsing "". */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (!options.noRetry) await ensureFresh();

  const body = bodyFor(options);
  const response = await fetch(API_BASE + path, {
    method: options.method ?? (body !== undefined ? 'POST' : 'GET'),
    headers: headersFor(options),
    ...(body !== undefined ? { body } : {}),
    ...(options.signal ? { signal: options.signal } : {})
  });

  if (response.status === 401 && !options.noRetry) {
    if (await recoverFrom401()) return request<T>(path, { ...options, noRetry: true });
  }

  if (!response.ok) throw await failureFrom(response);
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * A binary fetch through the same bearer-token and replay path.
 *
 * Needed because nothing in a browser that loads a URL by itself - an `<img>`, an `<a download>`, a
 * plain navigation - can send an Authorization header. Every export, PDF, installer and device file
 * download in this app therefore comes through here and is handed to the user as a blob.
 */
export async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  if (!options.noRetry) await ensureFresh();

  const response = await fetch(API_BASE + path, {
    method: options.method ?? 'GET',
    headers: headersFor(options),
    ...(options.signal ? { signal: options.signal } : {})
  });

  if (response.status === 401 && !options.noRetry) {
    if (await recoverFrom401()) return requestBlob(path, { ...options, noRetry: true });
  }

  if (!response.ok) throw await failureFrom(response);
  return response.blob();
}

/**
 * The message to show for a failed call.
 *
 * An `ApiError` already carries the server's sentence; anything else is a transport failure, and
 * saying so is the difference between "the CRM refused this" and "the CRM could not be reached".
 */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof DOMException && error.name === 'AbortError') return '';
  if (error instanceof Error && error.message) return `Could not reach the server: ${error.message}`;
  return 'Could not reach the server.';
}
